import { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, RefreshCw, Trash2, Eye, EyeOff } from "lucide-react";
import { AnalysisCard } from "@/components/AnalysisCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  const d = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

const DRAFT_FIELDS = [
  ["pattern", "Pattern", "text"], ["entry", "Entry", "number"], ["target", "Target", "number"],
  ["stop", "Stop", "number"], ["period_days", "Period (days)", "number"], ["confidence", "Confidence %", "number"],
];

export default function AdminSetups() {
  const [data, setData] = useState(null);
  const [symbol, setSymbol] = useState("nasdaq");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api("/analysis/admin/list").then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy("regen");
    try {
      const d = await api("/analysis/admin/generate", { method: "POST" });
      toast.success(`Generated ${d.generated} Intelligence analysis cards`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const draftSetup = async () => {
    setBusy("draft");
    try {
      const d = await api("/analysis/setups/draft", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol_id: symbol }),
      });
      setDraft(d);
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const publish = async () => {
    setBusy("publish");
    try {
      const { pattern, direction, entry, target, stop, period_days, confidence, narrative } = draft;
      await api(`/analysis/setups/${draft.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pattern, direction, entry: Number(entry), target: Number(target), stop: Number(stop),
                               period_days: Number(period_days), confidence: Number(confidence), narrative, published: true }),
      });
      toast.success("MBM Trade Setup published");
      setDraft(null);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const discard = async () => {
    try { await api(`/analysis/cards/${draft.id}`, { method: "DELETE" }); } catch { /* ignore */ }
    setDraft(null);
  };

  const togglePublish = async (c) => {
    try {
      await api(`/analysis/setups/${c.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ published: !c.published }),
      });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (c) => {
    try { await api(`/analysis/cards/${c.id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); }
  };

  if (!data) return <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading analysis desk…</div>;

  return (
    <div className="space-y-6" data-testid="admin-setups-tab">
      <div className="glass-card p-5 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm text-zinc-100 font-medium">AI Market Analysis Desk</div>
          <div className="text-[11px] text-zinc-500">Intelligence cards auto-refresh every 6 hours. Draft &amp; publish MBM Trade Setups here.</div>
        </div>
        <button onClick={regenerate} disabled={!!busy} className="btn-glass text-white px-4 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="regenerate-analysis-btn">
          {busy === "regen" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate Intelligence cards
        </button>
        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
          className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-xs text-zinc-200" data-testid="setup-symbol-select">
          {data.symbols.map((s) => <option key={s.id} value={s.id} className="bg-[#0A0A0A]">{s.name}</option>)}
        </select>
        <button onClick={draftSetup} disabled={!!busy} className="btn-solid px-4 py-2.5 text-xs font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="draft-setup-btn">
          {busy === "draft" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Draft Trade Setup
        </button>
      </div>

      {draft && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="setup-draft-editor">
          <div className="glass-card p-5 space-y-3">
            <div className="section-label text-[10px] text-[#e6c67a]">Draft — review before publishing</div>
            <div className="grid grid-cols-2 gap-3">
              {DRAFT_FIELDS.map(([k, label, type]) => (
                <div key={k}>
                  <div className="section-label text-[8px] text-zinc-500 mb-1">{label}</div>
                  <input type={type} value={draft[k] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                    className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200" data-testid={`draft-${k}-input`} />
                </div>
              ))}
              <div>
                <div className="section-label text-[8px] text-zinc-500 mb-1">Direction</div>
                <select value={draft.direction} onChange={(e) => setDraft((d) => ({ ...d, direction: e.target.value }))}
                  className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200">
                  {["bullish", "bearish", "neutral"].map((o) => <option key={o} value={o} className="bg-[#0A0A0A]">{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div className="section-label text-[8px] text-zinc-500 mb-1">Narrative</div>
              <textarea rows={4} value={draft.narrative} onChange={(e) => setDraft((d) => ({ ...d, narrative: e.target.value }))}
                className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none" data-testid="draft-narrative-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={publish} disabled={!!busy} className="btn-solid px-5 py-2.5 text-xs font-medium disabled:opacity-50" data-testid="publish-setup-btn">
                {busy === "publish" ? "Publishing…" : "Publish to members"}
              </button>
              <button onClick={discard} className="btn-glass text-white px-4 py-2.5 text-xs">Discard</button>
            </div>
          </div>
          <AnalysisCard card={{ ...draft, entry: Number(draft.entry), target: Number(draft.target), stop: Number(draft.stop),
                                period_days: Number(draft.period_days), confidence: Number(draft.confidence) }} badge="MBM TRADE SETUPS" />
        </div>
      )}

      <div className="glass-card p-5">
        <div className="section-label text-[10px] text-zinc-400 mb-4">All Cards</div>
        <div className="space-y-2">
          {data.cards.length === 0 && <div className="text-sm text-zinc-500 font-light">No analysis cards yet. Hit "Regenerate Intelligence cards" to create the first batch.</div>}
          {data.cards.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 text-[12px] border-b border-white/5 pb-2" data-testid={`analysis-row-${c.id}`}>
              <span className="text-zinc-100 font-medium w-28">{c.name}</span>
              <span className={`px-2 py-0.5 rounded-full border text-[10px] ${c.kind === "setup" ? "border-[#e6c67a]/40 text-[#e6c67a]" : "border-[#9fd3ff]/40 text-[#9fd3ff]"}`}>
                {c.kind === "setup" ? "Trade Setup" : "Intelligence"}
              </span>
              <span className="text-zinc-400 flex-1 min-w-[140px] truncate">{c.pattern} · {c.direction}</span>
              <span className={c.fresh ? "text-[#7ee0a1]" : "text-zinc-600"}>{c.fresh ? "active" : "expired"}</span>
              <span className="text-zinc-600">{new Date(c.generated_at).toLocaleString()}</span>
              {c.kind === "setup" && (
                <button onClick={() => togglePublish(c)} className="text-zinc-400 hover:text-white" title={c.published ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${c.id}`}>
                  {c.published ? <Eye className="h-4 w-4 text-[#7ee0a1]" /> : <EyeOff className="h-4 w-4" />}
                </button>
              )}
              <button onClick={() => remove(c)} className="text-zinc-500 hover:text-[#ff7a7a]" data-testid={`delete-card-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
